import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  schema?: object;
  image?: string;
}

const DEFAULT_IMAGE = 'https://readdy.ai/api/search-image?query=modern%20renovation%20company%20logo%20house%20tools%20minimalist%20clean%20professional%20swiss%20quality%20building%20construction&width=1200&height=630&seq=casarenovaplus-og&orientation=landscape';

export default function SEOHead({ title, description, keywords, canonical, schema, image }: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const ogImage = image || DEFAULT_IMAGE;
    const today = new Date().toISOString().split('T')[0];

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('last-modified', today);

    // Open Graph
    setMeta('og:type', 'website', 'property');
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:image', ogImage, 'property');
    setMeta('og:locale', 'fr_CH', 'property');
    setMeta('og:site_name', 'CasaReNovaPlus', 'property');

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    const schemaId = 'seo-page-schema';
    let schemaEl = document.getElementById(schemaId);
    if (schema) {
      if (!schemaEl) {
        schemaEl = document.createElement('script');
        schemaEl.id = schemaId;
        schemaEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaEl);
      }
      schemaEl.textContent = JSON.stringify(schema);
    } else if (schemaEl) {
      schemaEl.remove();
    }

    return () => {
      if (schemaEl) schemaEl.remove();
    };
  }, [title, description, keywords, canonical, schema, image]);

  return null;
}
